/** Hooks for Phase 4 deploy UI: env config, smoke tests, domains,
 *  env vars, deployments, health, alerts, readiness, database.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, ApiError } from '@/lib/api'
import { useToast } from '@/components/ui/toast'
import type {
  EnvVarStatus,
  SmokeTestResult,
  DomainInfo,
  EnvVarRecord,
  DeploymentRecord,
  HealthStatus,
  AlertRule,
  ReadinessResult,
  ProvisionedDatabase,
} from '@/types/deploy'

// ─── Env Status ───

export function useEnvStatus(deploymentId: string | undefined) {
  return useQuery({
    queryKey: ['env-status', deploymentId],
    queryFn: () =>
      apiFetch<{ env_status: EnvVarStatus }>(
        `/api/deployments/${deploymentId}/env-status`,
      ),
    enabled: !!deploymentId,
    staleTime: 10_000,
  })
}

export function useConfigureEnv(deploymentId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { project_id: string; project_path: string }) =>
      apiFetch<{ env_status: EnvVarStatus }>(
        `/api/deployments/${deploymentId}/configure-env`,
        { method: 'POST', body: JSON.stringify(params) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['env-status', deploymentId] })
    },
  })
}

// ─── Smoke Tests ───

export function useSmokeTestResults(deploymentId: string | undefined) {
  return useQuery({
    queryKey: ['smoke-test', deploymentId],
    queryFn: () =>
      apiFetch<{ smoke_test: SmokeTestResult | null }>(
        `/api/deployments/${deploymentId}/smoke-test/results`,
      ),
    enabled: !!deploymentId,
    refetchInterval: (query) =>
      query.state.data?.smoke_test === null ? 5_000 : false,
  })
}

export function useRunSmokeTest(deploymentId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ smoke_test: SmokeTestResult }>(
        `/api/deployments/${deploymentId}/smoke-test`,
        { method: 'POST', body: JSON.stringify({ triggered_by: 'manual' }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['smoke-test', deploymentId] })
    },
  })
}

// ─── Domains ───
//
// Repointed to the WORKING backend surface (dashboard/customer_domains_routes.py,
// issue #217) — keyed by BOTH project_id and app_id in the URL path, and by the
// domain STRING (not an id) for verify/delete. The old /api/deployments/domains
// surface never persisted writes.

/** One DNS record the backend says the customer must add (#220). */
interface ServerDnsRecord {
  purpose: string
  type: string
  name: string
  value: string
  ttl?: string
  status: string
  note?: string | null
}

/** Raw shape returned by dashboard/customer_domains_routes.py. */
interface ServerCustomDomain {
  id: string
  domain: string
  status: 'pending' | 'verifying' | 'verified' | 'active' | 'failed' | 'removed'
  target_kind: string
  expected_target: string
  cert_arn?: string | null
  cert_status?: string | null
  dns_records?: ServerDnsRecord[]
  last_error?: string | null
  created_at: string
  verified_at?: string | null
}

function toDomainInfo(d: ServerCustomDomain, projectId: string): DomainInfo {
  return {
    id: d.id,
    project_id: projectId,
    domain: d.domain,
    status: d.status,
    verified: d.status === 'verified' || d.status === 'active',
    ssl_provisioned: d.status === 'active',
    dns_target: d.expected_target,
    target_kind: d.target_kind,
    cert_status: d.cert_status ?? null,
    dns_records: d.dns_records ?? [],
    last_error: d.last_error ?? null,
    created_at: d.created_at,
  }
}

/**
 * True while any domain is still provisioning: DNS (pending/verifying) → SSL/ACM
 * (verified, cert PENDING_VALIDATION) → per-app CloudFront distribution. The
 * verified→active transition is driven entirely by the backend reconciler, so
 * the UI must keep polling (observe-only) to reflect it — the old 30s verify-poll
 * stopped at DNS-verified, leaving the "SSL certificate validation — Pending"
 * widget stuck until a manual refresh. Stops once every domain is terminal
 * (active/failed). Observe-only (no re-verify) so it can never race the verify
 * endpoint back from active → verified.
 */
export function domainsStillProvisioning(domains: { status: string }[]): boolean {
  return domains.some((d) => d.status !== 'active' && d.status !== 'failed')
}

export function useDeployDomains(
  projectId: string | undefined,
  appId: string | undefined,
) {
  return useQuery({
    queryKey: ['deploy-domains', projectId, appId],
    queryFn: async () => {
      const res = await apiFetch<{ domains: ServerCustomDomain[] }>(
        `/api/projects/${projectId}/apps/${appId}/domains`,
      )
      return { domains: res.domains.map((d) => toDomainInfo(d, projectId!)) }
    },
    enabled: !!projectId && !!appId,
    staleTime: 30_000,
    // Auto-advance the UI through DNS → SSL → active without a manual refresh.
    refetchInterval: (query) =>
      domainsStillProvisioning(query.state.data?.domains ?? []) ? 15_000 : false,
  })
}

export function useAddDeployDomain(
  projectId: string | undefined,
  appId: string | undefined,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (domain: string) => {
      const res = await apiFetch<{
        domain: ServerCustomDomain
        instructions: { type: string; host: string; value: string; ttl?: string }
      }>(`/api/projects/${projectId}/apps/${appId}/domains`, {
        method: 'POST',
        body: JSON.stringify({ domain }),
      })
      return { domain: toDomainInfo(res.domain, projectId!), instructions: res.instructions }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deploy-domains', projectId, appId] })
    },
  })
}

export function useVerifyDeployDomain(
  projectId: string | undefined,
  appId: string | undefined,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (domain: string) =>
      apiFetch<{
        domain: string
        status: string
        last_attempt_observed: string[]
        last_attempt_matched: boolean
      }>(
        `/api/projects/${projectId}/apps/${appId}/domains/${encodeURIComponent(domain)}/verify`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deploy-domains', projectId, appId] })
    },
  })
}

export function useRemoveDeployDomain(
  projectId: string | undefined,
  appId: string | undefined,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (domain: string) =>
      apiFetch<void>(
        `/api/projects/${projectId}/apps/${appId}/domains/${encodeURIComponent(domain)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deploy-domains', projectId, appId] })
    },
  })
}

// ─── Env Vars (deploy-scoped) ───

// #15 contract — env vars are keyed by environment only ('sandbox' | 'production').
// The old environment × slot double axis is gone (deployment_env_vars.slot dropped).
export function useDeployEnvVars(
  projectId: string | undefined,
  environment: string,
) {
  return useQuery({
    queryKey: ['deploy-env-vars', projectId, environment],
    queryFn: () =>
      apiFetch<{ env_vars: EnvVarRecord[] }>(
        `/api/deployments/env-vars/${projectId}?environment=${environment}`,
      ),
    enabled: !!projectId,
    staleTime: 15_000,
  })
}

export function useSetDeployEnvVar(projectId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      key: string
      value: string
      environment: string
    }) =>
      apiFetch<{ env_var: EnvVarRecord }>(
        `/api/deployments/env-vars/${projectId}`,
        { method: 'POST', body: JSON.stringify(params) },
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ['deploy-env-vars', projectId, variables.environment],
      })
    },
  })
}

export function useDeleteDeployEnvVar(projectId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { key: string; environment: string }) =>
      apiFetch<{ deleted: boolean }>(
        `/api/deployments/env-vars/${projectId}/${params.key}?environment=${params.environment}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ['deploy-env-vars', projectId, variables.environment],
      })
    },
  })
}

// ─── Deployment History ───

export function useDeployHistory(
  projectId: string | undefined,
  environment?: string,
) {
  return useQuery({
    queryKey: ['deploy-history', projectId, environment],
    queryFn: () => {
      const params = new URLSearchParams({ project_id: projectId! })
      if (environment) params.set('environment', environment)
      return apiFetch<{ deployments: DeploymentRecord[] }>(
        `/api/deployments?${params}`,
      )
    },
    enabled: !!projectId,
    staleTime: 10_000,
    // Plan 48 Phase 2 — a deploy/promote completes asynchronously (background
    // EKS deploy), so the new row appears in the mirror table after the POST
    // returns. Poll while the page is open + focused so the history refreshes
    // on completion without a manual reload. Pauses when the tab is hidden.
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  })
}

// ─── Active deployment (canonical) ───

export interface ActiveDeployment {
  id: string
  state: string | null
  url: string | null
  environment: string | null
}

/**
 * #35 — resolve the app's *active* deployment from the canonical
 * `qyngent.app_deployments` table (app-keyed), independent of the legacy
 * `qyngent.deployments` history list. This is what the observability panels
 * (Health / Smoke / Alerts) key on, so a sandbox deploy or a prod deploy with
 * no legacy mirror is still observable. Returns `{ deployment: null }` when the
 * app has no live (non-torn-down) deployment in that environment.
 */
export function useActiveDeployment(
  applicationId: string | undefined,
  environment: string,
) {
  return useQuery({
    queryKey: ['active-deployment', applicationId, environment],
    queryFn: () => {
      const params = new URLSearchParams({ environment })
      return apiFetch<{ deployment: ActiveDeployment | null }>(
        `/api/deployments/app/${applicationId}/active?${params}`,
      )
    },
    enabled: !!applicationId,
    staleTime: 10_000,
    // Mirror useDeployHistory: a deploy completes asynchronously, so poll while
    // the page is open + focused so the panels light up on completion.
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  })
}

// ─── Deployment settings (F5 / #36) ───

export interface DeploySettings {
  health_check_path: string
}

/**
 * Per-app deployment settings (currently the readiness-probe path). App-keyed
 * and environment-scoped; the stored value takes effect on the next deploy.
 */
export function useDeploySettings(
  applicationId: string | undefined,
  environment: string,
) {
  return useQuery({
    queryKey: ['deploy-settings', applicationId, environment],
    queryFn: () => {
      const params = new URLSearchParams({ environment })
      return apiFetch<{ settings: DeploySettings }>(
        `/api/deployments/app/${applicationId}/settings?${params}`,
      )
    },
    enabled: !!applicationId,
    staleTime: 30_000,
  })
}

export function useUpdateDeploySettings(applicationId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { environment: string; health_check_path: string }) =>
      apiFetch<{ settings: DeploySettings }>(
        `/api/deployments/app/${applicationId}/settings`,
        { method: 'PUT', body: JSON.stringify(vars) },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['deploy-settings', applicationId, vars.environment],
      })
    },
  })
}

export function useDeployRollback() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (deploymentId: string) =>
      apiFetch<{ deployment: DeploymentRecord }>(
        `/api/deployments/${deploymentId}/rollback`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deploy-history'] })
    },
  })
}

// Plan 48 Phase 5b — useDeployPromote (legacy Model-A POST /api/deployments/
// promote) removed. Promote is Model-B (useBuildPromote → /api/build/{id}/promote).

// ─── Plan 31 M1 — v2 deploy (Coolify + *.qyngent.com) ─────────────────

export interface V2DeployResponse {
  success: boolean
  url: string | null
  error: string | null
  deployment_id: string | null
  coolify_app_uuid: string | null
  dns_record_id: string | null
  /** Plan 37 — "deploying" when the deploy is still running in the background
   *  (slow/EKS path); the real outcome arrives via the `deploy_complete` build
   *  WS event. "succeeded"/"failed" for deploys that finished synchronously. */
  status?: 'deploying' | 'succeeded' | 'failed'
}

/** Fires POST /api/build/:id/deploy. Pair with useBuildStream to
 *  observe the deploy_started → deploy_stage → deploy_complete events
 *  that update buildStore.deployStarted/Completed/deploymentUrl. */
export function useV2BuildDeploy(buildId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    // Plan 48 Phase 2 — `environment` lets sandbox_promote tiers deploy a
    // persistent sandbox (`sandbox-<slug>`). Defaults to production server-side.
    mutationFn: (params: {
      app_slug?: string
      source_repo?: string
      branch?: string
      environment?: 'sandbox' | 'production'
    }) =>
      apiFetch<V2DeployResponse>(`/api/build/${buildId}/deploy`, {
        method: 'POST',
        body: JSON.stringify(params ?? {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deploy-history'] })
      // Trial counter advanced on success — invalidate the badge.
      qc.invalidateQueries({ queryKey: ['trial-state'] })
    },
  })
}

/** Plan 48 Phase 2 — POST /api/build/:id/promote. Promotes a build's sandbox
 *  deploy to production (no rebuild). 409 if the build was never sandboxed.
 *  The buildId is supplied per-call so the Deployments page can promote any row. */
export function useBuildPromote() {
  const qc = useQueryClient()
  const { addToast } = useToast()
  return useMutation({
    mutationFn: (buildId: string) =>
      apiFetch<V2DeployResponse>(`/api/build/${buildId}/promote`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    // Toasts live at the hook (useMutation) level so they ALWAYS fire — mutate()
    // -level callbacks are dropped if the component re-renders/unmounts during
    // the ~20s async promote, which is why no feedback showed (caught live).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deploy-history'] })
      qc.invalidateQueries({ queryKey: ['deployments'] })
      addToast({
        title: 'Promoting to production…',
        description:
          'Your sandbox build is deploying to your live production URL. This can take a minute — the history updates when it completes.',
        variant: 'success',
      })
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? (err.detail as { message?: string })?.message ?? err.message
          : err instanceof Error
            ? err.message
            : 'Promote failed'
      addToast({ title: 'Promote failed', description: msg, variant: 'error' })
    },
  })
}

/** Plan 48 — DELETE /api/build/:id/staging. Tears down the build app's sandbox
 *  environment, freeing a staging slot for the sandbox_promote tiers (their
 *  max_staging_environments cap). Owner-only (verify_project_access server-side).
 *  buildId is supplied per-call so the Deployments page can free any app's slot. */
export function useTeardownSandbox() {
  const qc = useQueryClient()
  const { addToast } = useToast()
  return useMutation({
    mutationFn: (buildId: string) =>
      apiFetch<{ success: boolean; torn_down: number; environment: string }>(
        `/api/build/${buildId}/staging`,
        { method: 'DELETE' },
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['deploy-history'] })
      qc.invalidateQueries({ queryKey: ['deployments'] })
      qc.invalidateQueries({ queryKey: ['deploy-surface'] })
      addToast({
        title: 'Sandbox torn down',
        description:
          res?.torn_down > 0
            ? 'The sandbox environment was removed and a staging slot is now free.'
            : 'No active sandbox found — the staging slot is free.',
        variant: 'success',
      })
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? (err.detail as { message?: string })?.message ?? err.message
          : err instanceof Error
            ? err.message
            : 'Teardown failed'
      addToast({ title: 'Teardown failed', description: msg, variant: 'error' })
    },
  })
}

/** Deployments UI restructure — POST /api/build/:id/teardown-production. Tears
 *  down the build app's PRODUCTION environment (the live surface). Destructive +
 *  irreversible; the Danger Zone gates it behind a type-to-confirm modal.
 *  Owner-only (verify_project_access server-side) and audit-logged. */
export function useTeardownProduction() {
  const qc = useQueryClient()
  const { addToast } = useToast()
  return useMutation({
    mutationFn: (buildId: string) =>
      apiFetch<{ success: boolean; torn_down: number; environment: string }>(
        `/api/build/${buildId}/teardown-production`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['deploy-history'] })
      qc.invalidateQueries({ queryKey: ['deployments'] })
      qc.invalidateQueries({ queryKey: ['deploy-surface'] })
      addToast({
        title: 'Production torn down',
        description:
          res?.torn_down > 0
            ? 'The production environment was removed.'
            : 'No active production environment was found.',
        variant: 'success',
      })
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? (err.detail as { message?: string })?.message ?? err.message
          : err instanceof Error
            ? err.message
            : 'Teardown failed'
      addToast({ title: 'Teardown failed', description: msg, variant: 'error' })
    },
  })
}

// ─── Plan 31 M4.5 — trial state badge ─────────────────────────────────

export interface TrialState {
  plan: 'free' | 'pro' | 'max' | 'ultra' | 'team' | 'enterprise' | 'anonymous' | string
  allowed: boolean
  used: number
  limit: number
  days_left: number
  reason?: string
  // Plan 48 — deployments tier-hybrid (served by /api/billing/trial-state).
  deploy_surface?: 'single' | 'sandbox_promote'
  staging_enabled?: boolean
  /** Included persistent-staging envs; null = unlimited. */
  max_staging_environments?: number | null
}

/** Fetches the current user's trial budget. Refetched after every
 *  deploy (via useV2BuildDeploy.onSuccess) and on focus.
 *  Stale-after 60s; consumers can opt out via `enabled: false`. */
export function useTrialState(enabled: boolean = true) {
  return useQuery({
    queryKey: ['trial-state'],
    queryFn: () => apiFetch<TrialState>('/api/billing/trial-state'),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

// ─── Plan 31 R1.5 — Stripe Connect ────────────────────────────────────

export interface StripeConnectStatus {
  account_id: string | null
  charges_enabled: boolean
  payouts_enabled: boolean
  details_submitted: boolean
}

export interface StripeConnectAccountResponse {
  account_id: string
  onboarding_url: string
  created: boolean
}

/** Pulls the user's Connect account status. Polls on focus + after
 *  the user returns from the onboarding redirect (?connect=done). */
export function useStripeConnectStatus(enabled: boolean = true) {
  return useQuery({
    queryKey: ['stripe-connect-status'],
    queryFn: () => apiFetch<StripeConnectStatus>('/api/billing/connect/status'),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

/** Creates the Express account (idempotent) and redirects to the
 *  one-time Stripe-hosted onboarding URL. */
export function useStripeConnectOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<StripeConnectAccountResponse>('/api/billing/connect/account', {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stripe-connect-status'] })
    },
  })
}

// ─── Health ───

export function useDeployHealth(deploymentId: string | undefined) {
  return useQuery({
    queryKey: ['deploy-health', deploymentId],
    queryFn: () =>
      apiFetch<{ health: HealthStatus }>(
        `/api/deployments/${deploymentId}/health`,
      ),
    enabled: !!deploymentId,
    refetchInterval: 30_000,
  })
}

// Deployments F1 — persisted auth detection for the Data & Auth tab.
export interface AuthStatus {
  detected: boolean
  provider: string | null
  required_env: string[]
  configured_env: string[]
  missing_env: string[]
  ready: boolean
  detected_files: string[]
}

/** GET /api/deployments/:id/auth-status. `auth: null` = deployment predates
 *  detection (redeploy to populate), NOT "no auth". */
export function useAuthStatus(deploymentId: string | undefined) {
  return useQuery({
    queryKey: ['deploy-auth-status', deploymentId],
    queryFn: () =>
      apiFetch<{ auth: AuthStatus | null }>(
        `/api/deployments/${deploymentId}/auth-status`,
      ),
    enabled: !!deploymentId,
  })
}

// F6 — resource metrics via a short-lived signed Grafana embed URL.
export function useMetricsEmbed(
  projectId: string | undefined,
  appId: string | undefined,
) {
  return useQuery({
    queryKey: ['metrics-embed', projectId, appId],
    queryFn: () =>
      apiFetch<{ url: string; expires_at: string }>(
        `/api/projects/${projectId}/apps/${appId}/metrics/embed?panel=overview`,
      ),
    enabled: !!projectId && !!appId,
    // The signed URL is short-lived; refetch before it expires.
    refetchInterval: 4 * 60_000,
    retry: false,
  })
}

// F4 — access logs (CloudWatch-backed) for a deployed app.
export interface AppLogEntry {
  timestamp: string
  level: string
  message: string
}

/** GET /api/projects/:pid/apps/:aid/logs — recent app logs from the LogStore
 *  (CloudWatch). `minutes` window + optional CW Insights `filter`. */
export function useAppLogs(
  projectId: string | undefined,
  appId: string | undefined,
  opts?: { minutes?: number; filter?: string; limit?: number; environment?: string },
) {
  const minutes = opts?.minutes ?? 15
  const filter = opts?.filter ?? ''
  const limit = opts?.limit ?? 100
  const environment = opts?.environment ?? 'production'
  return useQuery({
    queryKey: ['app-logs', projectId, appId, environment, minutes, filter, limit],
    queryFn: () =>
      apiFetch<{ entries: AppLogEntry[] }>(
        `/api/projects/${projectId}/apps/${appId}/logs?minutes=${minutes}` +
          `&filter=${encodeURIComponent(filter)}&limit=${limit}&environment=${environment}`,
      ),
    enabled: !!projectId && !!appId,
  })
}

/** GET /api/deployments/app/:id/auth-status — latest detection for an
 *  APPLICATION (its most recent analyzed deploy). App-keyed so the Data & Auth
 *  panel surfaces detection independent of activeDeploymentId (null on the
 *  sandbox slot / for failed deploys). `auth: null` = never analyzed. */
export function useLatestAuthStatus(applicationId: string | undefined) {
  return useQuery({
    queryKey: ['deploy-auth-status-app', applicationId],
    queryFn: () =>
      apiFetch<{ auth: AuthStatus | null }>(
        `/api/deployments/app/${applicationId}/auth-status`,
      ),
    enabled: !!applicationId,
  })
}

// ─── Alert Rules ───

export function useDeployAlertRules(deploymentId: string | undefined) {
  return useQuery({
    queryKey: ['deploy-alerts', deploymentId],
    queryFn: () =>
      apiFetch<{ alerts: AlertRule[] }>(`/api/alerts/${deploymentId}`),
    enabled: !!deploymentId,
  })
}

export function useCreateDeployAlert(deploymentId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (
      params: Omit<
        AlertRule,
        'id' | 'deployment_id' | 'active' | 'silenced_until' | 'created_at'
      >,
    ) =>
      apiFetch<{ alert: AlertRule; confirmation_sent?: boolean }>(
        `/api/alerts/${deploymentId}`,
        {
          method: 'POST',
          body: JSON.stringify(params),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deploy-alerts', deploymentId] })
    },
  })
}

export function useUpdateDeployAlert(deploymentId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { ruleId: string; active?: boolean }) =>
      apiFetch<{ updated: boolean }>(
        `/api/alerts/${deploymentId}/${vars.ruleId}`,
        { method: 'PATCH', body: JSON.stringify({ active: vars.active }) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deploy-alerts', deploymentId] }),
  })
}

export function useDeleteDeployAlert(deploymentId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: string) =>
      apiFetch<{ deleted: boolean }>(
        `/api/alerts/${deploymentId}/${ruleId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deploy-alerts', deploymentId] }),
  })
}

// ─── Readiness ───

export function useReadiness(deploymentId: string | undefined) {
  return useQuery({
    queryKey: ['deploy-readiness', deploymentId],
    queryFn: () =>
      apiFetch<{ readiness: ReadinessResult }>(
        `/api/deployments/${deploymentId}/readiness`,
      ),
    enabled: !!deploymentId,
  })
}

// ─── Database Provisioning ───

export function useDatabaseStatus(deploymentId: string | undefined) {
  return useQuery({
    queryKey: ['deploy-database', deploymentId],
    queryFn: () =>
      apiFetch<{ database: ProvisionedDatabase | null }>(
        `/api/deployments/${deploymentId}/database/status`,
      ),
    enabled: !!deploymentId,
    refetchInterval: (query) => {
      // Poll while a tenant DB exists but the app isn't wired to it yet.
      const db = query.state.data?.database
      return db?.status === 'provisioned' ? 10_000 : false
    },
  })
}
